'use client'
import { type FC, useMemo } from 'react'
import { usePathname } from 'next/navigation'
import styled from 'styled-components'
import { motion } from 'motion/react'
import { MODALS } from '@/context'
import { useFilters, useModal, useTranslator } from '@/hooks'
import { mq, vw } from '@/styles'
import { Button } from '../base/Button'
import { E_FILTER_TYPE } from '@/schema'

const getBooleanFilterToggleLabel = (currentFilters, activeFilters) => {
  // Get boolean filters from currentFilters
  const booleanFilters = currentFilters ? Object.entries(currentFilters).filter(([_key, filters]) => {
    // filters is the filter object, not an array
    const filter = Array.isArray(filters) ? filters[0] : filters
    return filter?.type === E_FILTER_TYPE.BOOLEAN
  }) : []

  return booleanFilters.map(([param, filters]) => {
    const filter = Array.isArray(filters) ? filters[0] : filters
    const inputId = `filter-${param}`  // Matches FormFilters input ID

    // Check if active by comparing filter.slug with activeFilter.param
    const isActive = activeFilters.some(activeFilter => activeFilter.param === filter.slug)

    return (
      <li key={param}>
        <Button
          type='button'
          className='btn btn--ac-f'
          onClick={() => {
            const checkbox = document.getElementById(inputId) as HTMLInputElement
            if (checkbox) checkbox.click()

            setTimeout(() => {
              window.scrollTo({ top: 160, left: 0, behavior: 'smooth' })
            }, 250)
          }}
          outline={!isActive}
          {...isActive ? {
            bgColor: '--color-text--darker',
            color: '--color-background'
          } : {
            bgColor: '--color-background',
            color: '--color-text--darker'
          }}
        >
          {filter.name}
        </Button>
      </li>
    )
  })
}


const getSelectFilterToggleLabel = (currentFilters, activeFilters, updatePayload) => {
  // Get SELECT filters from currentFilters (grouped by parent)
  const selectFilters = currentFilters ? Object.entries(currentFilters).filter(([key, filters]) => {
    if (key === 'brands') return false
    const filterArray = Array.isArray(filters) ? filters : [filters]
    return filterArray[0]?.type === E_FILTER_TYPE.SELECT
  }) : []

  return selectFilters.map(([param, filters]) => {
    const filterArray = Array.isArray(filters) ? filters : [filters]
    const parentName = filterArray[0]?.parentName  // e.g., "Regions", "Format"
    const parentSlug = filterArray[0]?.parentSlug

    // Count how many values are selected for this parent filter
    const activeFilter = activeFilters.find(af => af.param === parentSlug)
    const selectedCount = activeFilter && Array.isArray(activeFilter.value)
      ? activeFilter.value.length
      : activeFilter?.value ? 1 : 0

    return (
      <li key={param}>
        <Button
          type='button'
          className='btn btn--ac-f'
          onClick={() => {
            updatePayload({
              payload: {
                [param]: { expanded: true }
              },
              open: true
            })
          }}
          outline={selectedCount <= 0}
          {...selectedCount > 0 ? {
            bgColor: '--color-text--darker',
            color: '--color-background'
          } : {
            bgColor: '--color-background',
            color: '--color-text--darker'
          }}
        >
          {parentName}
          {selectedCount > 0 && <> ({selectedCount})</>}
        </Button>
      </li>
    )
  })
}



const headingId = 'section-active-filters'
export const SectionFilters:FC = () => {
  const { updatePayload } = useModal(MODALS.FILTERS)
  const { t } = useTranslator()
  // const [compactedGrid, _setCompactedGrid] = useState(false)
  const { currentFilters, activeFilters } = useFilters()
  const pathname = usePathname()

  const brandsSelected = useMemo(() => {
    const brandFilter = activeFilters.find(item => item.param === 'brand')
    return Array.isArray(brandFilter?.value) ? brandFilter.value.length : brandFilter?.value ? 1 : 0
  }, [activeFilters])

  const BooleanFilters = useMemo(() => getBooleanFilterToggleLabel(currentFilters, activeFilters), [currentFilters, activeFilters])

  const SelectFilters = useMemo(() =>
    getSelectFilterToggleLabel(currentFilters, activeFilters, updatePayload),
    [currentFilters, activeFilters, updatePayload]
  )


  const onClickOpenFilters = () => {
    updatePayload({ payload: null, open: true })
  }

  const onClickOpenFiltersWithOrderExpanded = () => {
    updatePayload({
      payload: {
        sort: { expanded: true }
      },
      open: true
    })
  }

  const onClickOpenFiltersWithBrandsExpanded = () => {
    updatePayload({
      payload: {
        brand: { expanded: true }
      },
      open: true
    })
  }

  return (
    <Section aria-labelledby={headingId} $compactedGrid={false} $productPage={pathname.includes('/product/')} initial={{ opacity: 0 }} animate={{ opacity: pathname.includes('/product/') ? 0 : 1 }} transition={{ duration: .3, ease: 'easeInOut' }}>
      <h2 id={headingId} className='srt-only'>Filters</h2>
      <Button className='btn bnt--trigger' onClick={onClickOpenFilters} aria-haspopup='dialog' aria-controls={MODALS.FILTERS}>
        {t('project.modals.filters.order_and_filter') as string}
      </Button>
      <Ul aria-live='polite'>
        <li>
          <Button className='btn btn--ac-f' onClick={onClickOpenFiltersWithOrderExpanded} outline={true} bgColor='--color-background' color='--color-text--darker'>
            {t('project.modals.filters.sort') as string}
          </Button>
        </li>
        {!pathname.includes('/brand/') &&
          <li>
            <Button className='btn btn--ac-f' onClick={onClickOpenFiltersWithBrandsExpanded} outline={brandsSelected <= 0} bgColor='--color-background' color='--color-text--darker' {...brandsSelected > 0 ? {
              bgColor: '--color-text--darker',
              color: '--color-background'
            } : {
              bgColor: '--color-background',
              color: '--color-text--darker'
            }}>
              {t('project.modals.filters.brands') as string}
              {brandsSelected > 0 && <> ({brandsSelected})</>}
            </Button>
          </li>
        }
        {BooleanFilters}
        {SelectFilters}
      </Ul>
    </Section>
  )
}

const Section = styled(motion.section)<{ $compactedGrid?: boolean, $productPage?: boolean }>`
  grid-column: 1 / span 6;
  grid-row: 1 / span 1;
  display: ${({ $productPage }) => $productPage ? 'none' : 'block'};
  margin-bottom: 30px;
  opacity: ${({ $productPage }) => $productPage ? 0 : 1};
  pointer-events: none;
  transition: opacity .250ms ease-in-out;
  z-index: 10;

  ${mq.greaterThan('tablet')} {
    grid-column: 4 / span 9;
		margin-bottom: 40px;
    position: sticky;
    top: 62px;
  }

  ul {
    display: none;

    ${mq.greaterThan('tablet')} {
      align-items: center;
      display: flex;
      gap: 10px;
      height: 45px;
    }
  }

  .bnt--trigger,
  .btn--ac-f {
    pointer-events: ${({ $productPage }) => $productPage ? 'none' : 'auto'};
    user-select: ${({ $productPage }) => $productPage ? 'none' : 'auto'};
  }

  .bnt--trigger {
    ${mq.greaterThan('tablet')} {
      display: none;
    }
  }

  .btn--ac-f {
    background-color: var(--color-background);
    font-weight: 400;
    height: ${vw(40, 'mobile')};
    padding: 0 ${vw(17, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      height: ${vw(40, 'tablet')};
      padding: 0 ${vw(17, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      height: ${vw(40, 'desktop')};
      padding: 0 ${vw(17, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      height: 40px;
      padding: 0px 17px;
    }

    span {
      white-space: nowrap;
    }
  }
`

const Ul = styled.ul`
`
